import { AnyAction } from 'redux';
import { AxiosResponse } from 'axios';

import { ContentLocalesI } from '../../interfaces/session.types';
import { CopySurvey, EditSurvey } from '../../modules/surveys/constants/actionTypes';
import {
  AddValue,
  EditAttribute,
  DeleteAttribute,
  EditValue,
  DeleteValue,
} from '../../modules/attributes/constants/actionTypes';
import { UserActionPayload } from '../../modules/users/actions/user.actions';
import {
  ADD_USER_SUCCESS,
  CHANGE_ACTIVE_STATUS_USER_SUCCESS,
  DELETE_USER_SUCCESS,
  UPDATE_USER_SUCCESS,
} from '../../modules/users/constants/actions';
import { EditAttrOptPayloadI, AttrOptionAddI } from '../../modules/attributes/actions/attributes.actions';
import { ThunkSuccessAction } from '../../services/apiMiddleware';
import { PartialBy } from '../../types';
import { translateContent } from '../../services/translationServices';
import {
  EDIT_TOPIC_SUCCESS,
  EDIT_QUESTION_SUCCESS,
  DELETE_QUESTION_SUCCESS,
  DELETE_TOPIC_SUCCESS,
  ADD_QUESTION_TOPIC_SUCCESS,
} from '../../modules/topics/constants/actionTypes';
import { EDIT_SCALE_SUCCESS, DELETE_SCALE_SUCCESS } from '../../modules/scales/constants/actionTypes';
import {
  UpdateCompany,
  PatchSessionUser,
  UpdateCompanyAttrs,
  UpdateSessionUserAttrs,
  ChangePassword,
} from '../session/actionTypes';
import { CopyUserSurvey } from '../../modules/reports/userProfile/constants/actions';
import { Survey, SurveyScopes } from '../../modules/surveys/interfaces';
import { DELETE_ANSWER_SUCCESS } from '../../modules/questions/constants/actionTypes';

import { SHOW_NOTIFICATION } from './actionTypes';
import { NotificationI } from './interfaces';

type NotificationSwitchPayloadI = PartialBy<NotificationI, 'id'>;

type NotificationAction = {
  payload: NotificationSwitchPayloadI;
} & AnyAction;

type NotificationActionSwitch = PartialBy<NotificationAction, 'payload'>;

export const notificationsActionsSwitcher = (action: AnyAction): NotificationAction | null => {
  const notifAction: NotificationActionSwitch = { type: SHOW_NOTIFICATION };
  let payload: NotificationSwitchPayloadI | undefined;
  let txtKey: string | undefined;
  let idSfx: string | undefined;

  switch (action.type) {
    // Company
    case UpdateCompany.Success:
    case UpdateCompanyAttrs.Success:
      payload = { txtKey: 'x.company_settings.message.saved_successfully' };
      break;
    // User
    case PatchSessionUser.Success:
    case UpdateSessionUserAttrs.Success:
      payload = { txtKey: 'x.profile_settings.settings_updated_message' };
      break;
    // User management
    case ADD_USER_SUCCESS:
      payload = getNamedPayload(
        'x.users.form.user_created_message',
        (action as ThunkSuccessAction<UserActionPayload>).fullName,
      );
      break;

    case DELETE_USER_SUCCESS:
      payload = getNamedPayload(
        'x.users.form.user_deleted_message',
        (action as ThunkSuccessAction<UserActionPayload>).fullName,
      );
      break;

    case CHANGE_ACTIVE_STATUS_USER_SUCCESS:
      payload = { txtKey: 'x.users.form.user_active_status_changed_message' };
      break;

    case UPDATE_USER_SUCCESS:
      payload = getNamedPayload(
        'x.users.form.user_updated_message',
        (action as ThunkSuccessAction<UserActionPayload>).fullName,
      );
      break;
    // Attributes
    case EditAttribute.Success:
      payload = { txtKey: 'x.attributes.details.details_updated_message' };
      break;

    case DeleteAttribute.Success:
      txtKey = 'x.attributes.attr_deleted_message';
      payload = {
        id: `${txtKey}.${action.slug}`,
        txtKey,
        txtProps: { name: action.slug },
      };
      break;

    case AddValue.Success:
      payload = !action.response.data.error
        ? getAttrOptPayload(action as AnyAction & AttrOptionAddI)
        : undefined;
      break;

    case EditValue.Success:
      payload = !action.response.data.error
        ? getAttrOptPayload(action as AnyAction & EditAttrOptPayloadI)
        : undefined;
      break;

    case DeleteValue.Success:
      txtKey = 'x.attributes.details.option.option_deleted_message';
      idSfx = translateContent(action.name);
      payload = {
        id: `${txtKey}.${idSfx}`,
        txtKey,
        txtProps: { name: idSfx },
      };
      break;
    // Surveys
    case EditSurvey.Success:
      payload = {
        txtKey: isAdminScope((action as ThunkSuccessAction<Survey>).response)
          ? 'x.surveys.details_updated_message'
          : 'x.surveys.request_details_updated_message',
      };
      break;

    case CopySurvey.Success:
      payload = getNamedPayload('x.surveys.copied_survey_message', action.response.data.name);
      break;
    // Topics
    case EDIT_TOPIC_SUCCESS:
      payload = getNamedPayload(
        action.payload.scaleId ? 'x.topics.topic_scale_edited_message' : 'x.topics.topic_edited_message',
        action.payload.name,
      );
      break;

    case DELETE_TOPIC_SUCCESS:
      payload = getNamedPayload('x.topics.topic_deleted_message', action.name);
      break;

    case ADD_QUESTION_TOPIC_SUCCESS:
      payload = action.showNotif
        ? getNamedPayload('x.topics.details.question_created_message', action.question.selfTitle)
        : undefined;
      break;

    case EDIT_QUESTION_SUCCESS:
      payload = getNamedPayload('x.topics.details.question_edited_message', action.payload.selfTitle);
      break;

    case DELETE_QUESTION_SUCCESS:
      payload = getNamedPayload('x.topics.details.question_deleted_message', action.name);
      break;
    // Scales
    case EDIT_SCALE_SUCCESS:
      payload = {
        txtKey: action.payload.name
          ? 'x.scale.details.scale_details_edited_message'
          : 'x.scale.details.scale_edited_message',
      };
      break;

    case DELETE_SCALE_SUCCESS:
      payload = getNamedPayload('x.scale.scale_deleted_message', action.name);
      break;
    // User profile
    case CopyUserSurvey.Success:
      payload = getNamedPayload(
        'x.reports.user.feedback_requests.request_copied_message',
        action.response.data.name,
      );
      break;
    // Questions
    case DELETE_ANSWER_SUCCESS:
      payload = { txtKey: 'x.questions.card.reset.success' };
      break;

    case ChangePassword.Success:
      payload = { txtKey: 'x.profile_settings.password_updated_message' };
      break;
    case ChangePassword.Failed:
      payload = { txtKey: action.response.data.error };
      break;
  }

  if (payload) {
    notifAction.payload = payload;
    notifAction.payload.id = notifAction.payload.id || notifAction.payload.txtKey;
    return notifAction as NotificationAction;
  }

  return null;
};

const getNamedPayload = (txtKey: string, name: string | ContentLocalesI): NotificationSwitchPayloadI => {
  name = typeof name === 'object' ? translateContent(name) : name;
  return { id: `${txtKey}.${name}`, txtKey, txtProps: { name } };
};

const getAttrOptPayload = (
  attrOptAction: AttrOptionAddI | EditAttrOptPayloadI,
): NotificationSwitchPayloadI => {
  let attrOptions: AttrOptionAddI['attributes'];
  let txtKey: string | undefined;
  const isOptAddI = (optAction: AttrOptionAddI | EditAttrOptPayloadI): optAction is AttrOptionAddI => {
    return (optAction as AttrOptionAddI).attributes !== undefined;
  };

  if (isOptAddI(attrOptAction)) {
    txtKey = 'x.attributes.details.option.option_added_message';
    attrOptions = attrOptAction.attributes;
  } else {
    txtKey = 'x.attributes.details.option.option_updated_message';
    attrOptions = attrOptAction.attribute_options;
  }

  const optName = translateContent(
    Object.values(attrOptions)[0] as {
      [key: string]: string;
    },
  );

  const notifPayload: NotificationSwitchPayloadI = {
    id: `${txtKey}.${optName}`,
    txtKey,
    txtProps: { name: optName },
  };
  if (attrOptAction.users && attrOptAction.users.length) {
    notifPayload.txtProps!.count = attrOptAction.users.length;
    notifPayload.txtKey = isOptAddI(attrOptAction)
      ? 'x.attributes.details.option.option_added_people_message'
      : 'x.attributes.details.option.option_updated_people_message';
  }
  return notifPayload;
};

const isAdminScope = ({ data }: AxiosResponse<Survey>): boolean => {
  return data && data.scope === SurveyScopes.Admin;
};
